export type InvoiceRole = 'deposit' | 'works' | 'variation' | 'credit';

export const INVOICE_ROLES: InvoiceRole[] = ['deposit', 'works', 'variation', 'credit'];

export function normalizeInvoiceRole(value?: string | null): InvoiceRole | null {
  const normalized = String(value || '').trim().toLowerCase();
  return (INVOICE_ROLES as string[]).includes(normalized) ? normalized as InvoiceRole : null;
}

/**
 * VAT for invoices raised from a quote. The pricing header of the quote's
 * active survey (Final once it exists, otherwise First) is the source of truth;
 * the quote's `documents` row can carry a stale `vat_enabled` while its stored
 * totals still include VAT.
 */
export function resolveSourceVat(
  source: { vatEnabled?: boolean | null; vatPct?: number | null; vatAmount?: number | null },
  header?: { vat_enabled?: boolean | null; vat_pct?: number | null } | null,
): { vatEnabled: boolean; vatPct: number } {
  const vatPct = header?.vat_pct != null
    ? Number(header.vat_pct)
    : (source.vatPct != null ? Number(source.vatPct) : 0.2);
  if (header?.vat_enabled != null) return { vatEnabled: !!header.vat_enabled, vatPct };
  const vatEnabled = source.vatEnabled !== false || Number(source.vatAmount || 0) > 0;
  return { vatEnabled, vatPct };
}

export function invoiceRoleLabel(role?: string | null): string {
  const normalized = normalizeInvoiceRole(role);
  if (normalized === 'deposit') return 'Deposit';
  if (normalized === 'works') return 'Balance';
  if (normalized === 'variation') return 'Variation';
  if (normalized === 'credit') return 'Credit';
  return 'Invoice';
}

/**
 * Invoice workspace/list status. "Sent" is customer transmission only —
 * FreeAgent issue must never surface as Sent.
 */
export function invoiceDisplayStatus(args: {
  status?: string | null;
  customerTransmitted?: boolean;
}): {
  label: string;
  color: 'success' | 'primary' | 'neutral' | 'danger';
} {
  const status = String(args.status || 'draft').trim().toLowerCase();
  if (status === 'paid' || status === 'cash' || status === 'accepted') {
    return {
      label: status === 'paid' ? 'Paid' : status.charAt(0).toUpperCase() + status.slice(1),
      color: 'success',
    };
  }
  if (status === 'rejected' || status === 'void') {
    return { label: status.charAt(0).toUpperCase() + status.slice(1), color: 'danger' };
  }
  if (args.customerTransmitted) return { label: 'Sent', color: 'primary' };
  if (status === 'sent') return { label: 'Draft', color: 'neutral' };
  return {
    label: status.charAt(0).toUpperCase() + status.slice(1),
    color: 'neutral',
  };
}

export function invoiceShouldStayFrozen(args: {
  invoiceRole?: string | null;
  frozenAt?: string | null;
  status?: string | null;
  isOriginal?: boolean | null;
}): boolean {
  if (args.isOriginal) return true;
  if (args.frozenAt) return true;
  const role = normalizeInvoiceRole(args.invoiceRole);
  if (role === 'deposit' || role === 'credit') return true;
  const status = String(args.status || '').trim().toLowerCase();
  return status === 'sent' || status === 'paid' || status === 'accepted' || status === 'cash' || status === 'void';
}

export function invoiceCanBeLifecycleSynced(args: {
  invoiceRole?: string | null;
  frozenAt?: string | null;
  status?: string | null;
  isOriginal?: boolean | null;
}): boolean {
  if (invoiceShouldStayFrozen(args)) return false;
  const role = normalizeInvoiceRole(args.invoiceRole);
  const status = String(args.status || '').trim().toLowerCase();
  return role === 'works' && status === 'draft';
}

/**
 * Whether a quote-linked invoice may have its amounts edited by hand. Only drafts that the
 * quote sync never rewrites (deposit, credit, variation, frozen balance) and that are not yet
 * in FreeAgent. Draft balances that still follow the quote stay locked to it.
 */
export function invoiceAmountEditable(args: {
  invoiceRole?: string | null;
  frozenAt?: string | null;
  status?: string | null;
  isOriginal?: boolean | null;
  freeagentInvoiceUrl?: string | null;
}): boolean {
  if (args.isOriginal) return true;
  if (String(args.freeagentInvoiceUrl || '').trim()) return false;
  if (String(args.status || '').trim().toLowerCase() !== 'draft') return false;
  if (!normalizeInvoiceRole(args.invoiceRole)) return false;
  return !invoiceCanBeLifecycleSynced(args);
}

/** Balance / variation / credit invoices already store the billed amount. Do not re-split deposit. */
export function invoicePdfUsesOwnTotals(role?: string | null): boolean {
  const normalized = normalizeInvoiceRole(role);
  return normalized === 'works' || normalized === 'variation' || normalized === 'credit';
}

/**
 * Whether the export template should render a deposit totals row (Deposit 40% / Less: deposit).
 * A deposit invoice IS the deposit, so it never repeats that row. Balance / variation / credit
 * invoices only show it for the "including deposit" costume, using the sibling deposit figures.
 * Quotes still use the document's hasDeposit flag.
 */
export function invoicePdfShowsDepositBreakdown(args: {
  kind?: string | null;
  invoiceRole?: string | null;
  scenario?: string | null;
}): boolean {
  const scenario = String(args.scenario || '').trim();
  if (
    scenario === 'deposit_only'
    || scenario === 'outstanding_only'
    || scenario === 'total_without_deposit_breakdown'
    || scenario === 'no_vat_no_deposit'
    || scenario === 'stage_only'
  ) {
    return false;
  }
  if (String(args.kind || '') === 'Invoice') {
    const role = normalizeInvoiceRole(args.invoiceRole);
    if (role === 'deposit') return false;
    if (invoicePdfUsesOwnTotals(role)) return scenario === 'total_with_deposit_breakdown';
  }
  return true;
}

/** Excluding-deposit costume (export default and Send) notes the sibling deposit outside the table. */
export function invoicePdfShowsPriorDepositReference(args: {
  kind?: string | null;
  invoiceRole?: string | null;
  scenario?: string | null;
}): boolean {
  if (String(args.kind || '') !== 'Invoice') return false;
  if (!invoicePdfUsesOwnTotals(args.invoiceRole)) return false;
  const scenario = String(args.scenario || '').trim();
  return !scenario || scenario === 'total_without_deposit_breakdown';
}

export function priorDepositInvoiceAmount(invoice?: {
  grossTotal?: number | null;
  depositAmount?: number | null;
} | null): number {
  if (!invoice) return 0;
  const gross = money(invoice.grossTotal);
  if (gross > 0) return gross;
  return money(invoice.depositAmount);
}

export function priorDepositInvoiceNarrative(args: {
  amount?: number | null;
  invoiceRef?: string | null;
  depositPercentage?: number | null;
}): string {
  const amount = money(args.amount);
  if (!(amount > 0)) return '';
  const amountBit = amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
  const rawPct = Number(args.depositPercentage);
  const pct = rawPct > 0 && rawPct <= 1 ? Math.round(rawPct * 100) : Math.round(rawPct);
  const pctBit = pct > 0 ? ` (${pct}%)` : '';
  const ref = String(args.invoiceRef || '').trim();
  const refBit = ref ? ` on ${ref}` : '';
  return `A deposit of ${amountBit}${pctBit} has already been invoiced${refBit}.`;
}

export function depositInvoiceLineName(args: {
  quotedGross?: number | null;
  depositPercentage?: number | null;
  fallback?: string | null;
}): string {
  const quoted = money(args.quotedGross);
  const pct = Number(args.depositPercentage);
  const quotedBit = quoted > 0
    ? quoted.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
    : '';
  if (quoted > 0 && pct > 0) {
    return `Deposit (${Math.round(pct * 100)}% of quoted project value ${quotedBit})`;
  }
  if (quoted > 0) {
    return `Deposit of quoted project value ${quotedBit}`;
  }
  return String(args.fallback || 'Deposit').trim() || 'Deposit';
}

export function defaultInvoicePdfScenario(role?: string | null): 'total_with_deposit_breakdown' | 'total_without_deposit_breakdown' {
  const normalized = normalizeInvoiceRole(role);
  if (normalized === 'deposit') return 'total_with_deposit_breakdown';
  return 'total_without_deposit_breakdown';
}

/** Send uses the same costume as the role's only/default export option. */
export function invoicePublicViewShowsDepositBreakdown(role?: string | null): boolean {
  return invoicePdfShowsDepositBreakdown({
    kind: 'Invoice',
    invoiceRole: role,
    scenario: defaultInvoicePdfScenario(role),
  });
}

export function invoiceAllowsPdfScenario(
  role: string | null | undefined,
  scenario: string,
): boolean {
  const normalized = normalizeInvoiceRole(role);
  if (normalized === 'deposit') {
    return scenario === 'total_with_deposit_breakdown';
  }
  if (invoicePdfUsesOwnTotals(normalized)) {
    return scenario !== 'deposit_only' && scenario !== 'outstanding_only';
  }
  return true;
}

export function invoicePdfDisplayGross(args: {
  invoiceRole?: string | null;
  scenario?: string | null;
  grandTotal?: number | null;
  depositAmount?: number | null;
}): number {
  const billed = money(args.grandTotal);
  const deposit = money(args.depositAmount);
  const scenario = String(args.scenario || '').trim();
  if (scenario === 'deposit_only') return deposit > 0 ? deposit : billed;
  if (invoicePdfUsesOwnTotals(args.invoiceRole)) return billed;
  if (scenario === 'outstanding_only') return Math.max(money(billed - deposit), 0);
  return billed;
}

function money(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round((n + Number.EPSILON) * 100) / 100 : 0;
}

export function resolveQuotedGrossForDeposit(args: {
  quotedGross?: number | null;
  depositAmount?: number | null;
  depositPercentage?: number | null;
}): number {
  const quoted = money(args.quotedGross);
  if (quoted > 0) return quoted;
  const deposit = money(args.depositAmount);
  const pct = Number(args.depositPercentage);
  if (deposit > 0 && pct > 0) return money(deposit / pct);
  return 0;
}

export function depositInvoiceNarrative(args: {
  lifecycleRef?: string | null;
  quotedGross?: number | null;
  sourceKind?: string | null;
}): string {
  const ref = String(args.lifecycleRef || '').trim();
  const kind = String(args.sourceKind || '').trim().toLowerCase() === 'estimate' ? 'Estimate' : 'Quotation';
  const against = ref ? `${kind} ${ref}` : kind.toLowerCase();
  const quoted = money(args.quotedGross);
  const valueBit = quoted > 0
    ? `, total quoted value ${quoted.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}`
    : '';
  return [
    `Deposit payment against ${against}${valueBit}.`,
    'Remaining works will be invoiced separately in accordance with the final agreed project value.',
  ].join('\n');
}

export function splitInclusiveAmount(
  gross: number,
  vatEnabled: boolean,
  vatPct: number,
): { net: number; vat: number; gross: number } {
  const inclusive = money(gross);
  if (!vatEnabled || !(Number(vatPct) > 0)) {
    return { net: inclusive, vat: 0, gross: inclusive };
  }
  const net = money(inclusive / (1 + Number(vatPct)));
  return { net, vat: money(inclusive - net), gross: inclusive };
}

export type LedgerInvoice = {
  id?: string;
  role?: string | null;
  status?: string | null;
  grossTotal?: number | null;
  depositAmount?: number | null;
};

export function invoicedToDate(invoices: LedgerInvoice[]): number {
  return invoices.reduce((sum, invoice) => {
    if (String(invoice.status || '').trim().toLowerCase() === 'void') return sum;
    const role = normalizeInvoiceRole(invoice.role);
    const gross = money(invoice.grossTotal);
    if (role === 'credit') return sum - Math.abs(gross);
    if (role === 'deposit' || role === 'works' || role === 'variation') return sum + gross;
    return sum + gross;
  }, 0);
}

export function depositInvoiced(invoices: LedgerInvoice[]): number {
  return invoices.reduce((sum, invoice) => {
    if (String(invoice.status || '').trim().toLowerCase() === 'void') return sum;
    if (normalizeInvoiceRole(invoice.role) !== 'deposit') return sum;
    const gross = money(invoice.grossTotal);
    return sum + (gross > 0 ? gross : money(invoice.depositAmount));
  }, 0);
}

export function computeInvoiceDelta(args: {
  settledContract: number;
  invoices: LedgerInvoice[];
}): {
  settledContract: number;
  alreadyInvoiced: number;
  depositInvoiced: number;
  delta: number;
  nextRole: InvoiceRole | null;
} {
  const settledContract = money(args.settledContract);
  const alreadyInvoiced = invoicedToDate(args.invoices);
  const deposit = depositInvoiced(args.invoices);
  const delta = money(settledContract - alreadyInvoiced);
  const hasWorks = args.invoices.some((invoice) => {
    const role = normalizeInvoiceRole(invoice.role);
    return (role === 'works' || role === 'variation') && String(invoice.status || '').toLowerCase() !== 'void';
  });
  let nextRole: InvoiceRole | null = null;
  if (delta > 0) nextRole = hasWorks ? 'variation' : 'works';
  if (delta < 0) nextRole = 'credit';
  return {
    settledContract,
    alreadyInvoiced,
    depositInvoiced: deposit,
    delta,
    nextRole,
  };
}

export function outstandingForEmail(args: {
  settledContract: number;
  invoices: LedgerInvoice[];
  finalSettled?: boolean;
}): number {
  const settled = money(args.settledContract);
  if (args.finalSettled) {
    return Math.max(money(settled - invoicedToDate(args.invoices)), 0);
  }
  return Math.max(money(settled - depositInvoiced(args.invoices)), 0);
}

export function invoiceSequenceRank(role?: string | null): number {
  const normalized = normalizeInvoiceRole(role);
  if (normalized === 'deposit') return 0;
  if (normalized === 'works') return 1;
  if (normalized === 'variation') return 2;
  if (normalized === 'credit') return 3;
  return 1;
}

export function sortInvoicesForSequence<T extends { role?: string | null; createdAt?: string | null }>(
  invoices: T[],
): T[] {
  return [...invoices].sort((a, b) => {
    const rank = invoiceSequenceRank(a.role) - invoiceSequenceRank(b.role);
    if (rank !== 0) return rank;
    return Date.parse(String(a.createdAt || '')) - Date.parse(String(b.createdAt || ''));
  });
}

export function formatInvoiceSequence(sequence: number): string {
  return String(Math.max(1, Math.floor(Number(sequence) || 1))).padStart(2, '0');
}

export function formatInvoiceRef(lifecycleRef: string, sequence: number): string {
  const base = String(lifecycleRef || '').trim();
  if (!base) return '';
  return `${base}-${formatInvoiceSequence(sequence)}`;
}

export function parseInvoiceRefSequence(invoiceRef: string, lifecycleRef: string): number | null {
  const base = String(lifecycleRef || '').trim();
  const ref = String(invoiceRef || '').trim();
  if (!base || !ref.startsWith(`${base}-`)) return null;
  const suffix = ref.slice(base.length + 1);
  if (!/^\d+$/.test(suffix)) return null;
  return Number(suffix);
}

export function nextInvoiceSequence(
  existingRefs: Array<string | null | undefined>,
  lifecycleRef: string,
): number {
  let max = 0;
  for (const ref of existingRefs) {
    const seq = parseInvoiceRefSequence(String(ref || ''), lifecycleRef);
    if (seq != null && seq > max) max = seq;
  }
  return max + 1;
}

function liveInvoiceRole(invoice: { invoiceRole?: string | null; role?: string | null; status?: string | null }) {
  if (String(invoice.status || '').trim().toLowerCase() === 'void') return null;
  return normalizeInvoiceRole(invoice.invoiceRole ?? invoice.role);
}

export function invoiceDeleteAllowed(
  invoice: { invoiceRole?: string | null; role?: string | null; status?: string | null },
  invoices: Array<{ invoiceRole?: string | null; role?: string | null; status?: string | null }>,
): boolean {
  const role = liveInvoiceRole(invoice);
  if (!role && String(invoice.status || '').trim().toLowerCase() === 'void') return true;
  const liveRoles = invoices.map((row) => liveInvoiceRole(row)).filter(Boolean);
  const hasVariation = liveRoles.includes('variation');
  const hasWorks = liveRoles.includes('works');
  if (role === 'variation' || role === 'credit') return true;
  if (role === 'works') return !hasVariation;
  if (role === 'deposit') return !hasWorks && !hasVariation;
  return !hasVariation && !hasWorks;
}

export function canCreateVariationInvoice(
  invoices: Array<{ invoiceRole?: string | null; role?: string | null; status?: string | null }>,
): boolean {
  return invoices.some((invoice) => liveInvoiceRole(invoice) === 'works');
}

/**
 * Invoice-tab header action. Before Final Balance issuance this stays
 * Create Invoice and is not used for the deposit/balance sequence.
 * After issuance it becomes the explicit post-settlement Variation/Credit action.
 */
export function invoiceTabCreateButton(args: {
  finalBalanceIssued: boolean;
}): {
  label: 'Create Invoice' | 'Create Variation/Credit';
  enabled: boolean;
  postSettlement: boolean;
} {
  if (args.finalBalanceIssued) {
    return {
      label: 'Create Variation/Credit',
      enabled: true,
      postSettlement: true,
    };
  }
  return {
    label: 'Create Invoice',
    enabled: false,
    postSettlement: false,
  };
}

export function documentDisplayRef(doc: {
  kind?: string | null;
  invoiceRef?: string | null;
  lifecycleRef?: string | null;
}): string {
  const invoiceRef = String(doc.invoiceRef || '').trim();
  if (String(doc.kind || '') === 'Invoice' && invoiceRef) return invoiceRef;
  return String(doc.lifecycleRef || invoiceRef || '').trim();
}

export function preferredEventInvoice<T extends { role?: string | null; kind?: string | null }>(
  invoices: T[],
  eventType: 'depositRequest' | 'outstandingPayment',
): T | null {
  if (eventType === 'depositRequest') {
    return invoices.find((invoice) => normalizeInvoiceRole(invoice.role) === 'deposit') || null;
  }
  return invoices.find((invoice) => {
    const role = normalizeInvoiceRole(invoice.role);
    return role === 'works' || role === 'variation';
  }) || invoices.find((invoice) => invoice.kind === 'Invoice') || null;
}

/**
 * A sent final balance can be reissued: the original is voided (kept on record) and a
 * replacement balance carries any agreed deductions. The replacement points back to it
 * through source_survey_event.
 */
export const BALANCE_REISSUE_EVENT_PREFIX = 'reissue-of:';

export function reissuedFromInvoiceId(sourceSurveyEvent?: string | null): string | null {
  const event = String(sourceSurveyEvent || '').trim();
  return event.startsWith(BALANCE_REISSUE_EVENT_PREFIX)
    ? event.slice(BALANCE_REISSUE_EVENT_PREFIX.length) || null
    : null;
}

/** Deduction lines are negative lines on a reissued balance, flagged in the line's meta. */
export function isDeductionLine(line: { metaJson?: any; meta_json?: any }): boolean {
  const meta = line.metaJson ?? line.meta_json;
  return !!meta && meta.deduction === true;
}

/**
 * Only the final balance, once it has gone out (sent, or rejected) and before it is paid.
 * Interim schedule stages are adjusted on the Payment Schedule instead.
 */
export function balanceReissueAllowed(args: {
  invoice: { id: string; invoiceRole?: string | null; status?: string | null };
  stages?: Array<{ kind: string; invoiceId?: string | null }> | null;
}): boolean {
  if (normalizeInvoiceRole(args.invoice.invoiceRole) !== 'works') return false;
  const status = String(args.invoice.status || '').trim().toLowerCase();
  if (status !== 'sent' && status !== 'rejected') return false;
  const stage = (args.stages || []).find((item) => item.invoiceId === args.invoice.id);
  return !stage || stage.kind === 'final';
}

/** What a reissued balance carries: its deductions and the voided invoice it replaces. */
export type BalanceAdjustment = {
  deductions: Array<{ name: string; amount: number }>;
  replacesRef: string | null;
};

export function totalBalanceDeductions(adjustments: Record<string, BalanceAdjustment>): number {
  const total = Object.values(adjustments)
    .reduce((sum, item) => sum + item.deductions.reduce((inner, row) => inner + Math.abs(Number(row.amount) || 0), 0), 0);
  return Math.round((total + Number.EPSILON) * 100) / 100;
}

/** The deduction as entered (VAT inclusive), from the line's meta; older lines fall back to the line total. */
export function deductionAmount(line: {
  metaJson?: any;
  meta_json?: any;
  lineTotal?: number | null;
  line_total?: number | null;
  qty?: number | null;
  unitPrice?: number | null;
  unit_price?: number | null;
}): number {
  const meta = line.metaJson ?? line.meta_json;
  const entered = Number(meta?.amount);
  if (Number.isFinite(entered) && entered > 0) return money(entered);
  const total = line.lineTotal ?? line.line_total;
  return money(Math.abs(total != null ? Number(total) : Number(line.qty || 0) * Number(line.unitPrice ?? line.unit_price ?? 0)));
}

/**
 * Net amounts for deduction lines so a reissued balance totals exactly balance − deductions.
 * Totals round each line's net separately, so VAT-inclusive negative lines could leave it a
 * penny out; these nets land where a single line of the target amount would.
 */
export function deductionLineNets(args: {
  keptLines: Array<{ lineTotal?: number | null; qty?: number | null; unitPrice?: number | null; vatInclusive?: boolean | null }>;
  amounts: number[];
  vatEnabled: boolean;
  vatPct: number;
}): number[] {
  const netOf = (gross: number, inclusive: boolean) => money(inclusive && args.vatEnabled ? gross / (1 + args.vatPct) : gross);
  const grossOf = (line: { lineTotal?: number | null; qty?: number | null; unitPrice?: number | null }) => (
    line.lineTotal != null ? Number(line.lineTotal) : Number(line.qty || 0) * Number(line.unitPrice || 0)
  );
  const keptGross = args.keptLines.reduce((sum, line) => sum + grossOf(line), 0);
  const keptNet = args.keptLines.reduce((sum, line) => sum + netOf(grossOf(line), !!line.vatInclusive), 0);
  const deducted = args.amounts.reduce((sum, amount) => sum + amount, 0);
  if (!(deducted > 0)) return args.amounts.map(() => 0);
  const deductionNet = money(netOf(money(keptGross - deducted), true) - keptNet);
  // Spread in proportion, with any rounding on the last deduction
  let left = deductionNet;
  return args.amounts.map((amount, index) => {
    if (index === args.amounts.length - 1) return money(left);
    const share = money(deductionNet * (amount / deducted));
    left = money(left - share);
    return share;
  });
}

/**
 * A variation or credit raised by hand (Create Variation/Credit) is an agreed extra on top of
 * the quote. One raised from the settled-contract delta carries the event that produced it and
 * is already part of the quote total.
 */
export function isManualAdjustment(invoice: {
  role?: string | null;
  invoiceRole?: string | null;
  status?: string | null;
  sourceSurveyEvent?: string | null;
}): boolean {
  if (String(invoice.status || '').trim().toLowerCase() === 'void') return false;
  const role = normalizeInvoiceRole(invoice.invoiceRole ?? invoice.role);
  if (role !== 'variation' && role !== 'credit') return false;
  return !String(invoice.sourceSurveyEvent || '').trim();
}

/** Hand-raised variations less hand-raised credits: what they add to the agreed total. */
export function manualAdjustmentTotal(invoices: Array<{
  role?: string | null;
  invoiceRole?: string | null;
  status?: string | null;
  grossTotal?: number | null;
  sourceSurveyEvent?: string | null;
}>): number {
  return money(invoices.filter(isManualAdjustment).reduce((sum, invoice) => {
    const amount = Math.abs(money(Number(invoice.grossTotal) || 0));
    return normalizeInvoiceRole(invoice.invoiceRole ?? invoice.role) === 'credit' ? sum - amount : sum + amount;
  }, 0));
}
